import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Download, Plus, RefreshCw, Save, Search, X } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { db } from "../firebase";
import {
  loadDiscountRowsForGroup,
  loadDiscountRowsForParty,
  loadAllPartyDiscountRows,
  normalizeGroupRow,
  normalizePartyRow,
  savePartyDiscountAmount,
  PARTY_DISCOUNTS_COLLECTION,
} from "../helpers/partyDiscountAmounts";

function parseAmountInput(raw) {
  const s = String(raw ?? "").trim();
  if (s === "") return 0;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

function fmtAmt(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function buildListFromRows(rows, partyList, groupList) {
  const partyMap = new Map(partyList.map((p) => [p.id, p]));
  const groupMap = new Map(groupList.map((g) => [g.id, g]));
  const nextDrafts = {};
  const nextList = [];
  for (const r of rows) {
    const key = `${r.partyId}__${r.groupId}`;
    const party = partyMap.get(r.partyId);
    const group = groupMap.get(r.groupId);
    nextList.push({
      partyId: r.partyId,
      groupId: r.groupId,
      partyName: r.partyName || party?.partyName || r.partyId,
      groupName: r.groupName || group?.name || r.groupId,
      city: party?.city || "",
      gst: party?.gst || "",
    });
    nextDrafts[key] = {
      discountAmount: String(r.discountAmount ?? ""),
      note: r.note || "",
      dirty: false,
    };
  }
  return { nextList, nextDrafts };
}

/**
 * One page: Group search + Party search → list below.
 * “Set discount” opens a full popup (group OR party + amount + Save). No tabs.
 */
export default function PartyDiscountAmountsPage() {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const [parties, setParties] = useState([]);
  const [groups, setGroups] = useState([]);
  const partiesRef = useRef([]);
  const groupsRef = useRef([]);

  /** Active filter: last search used */
  const [groupQuery, setGroupQuery] = useState("");
  const [partyQuery, setPartyQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState(/** @type {"none"|"group"|"party"} */ ("none"));

  const [matchedGroupId, setMatchedGroupId] = useState("");
  const [matchedPartyId, setMatchedPartyId] = useState("");

  /** List drafts keyed by rowKey (partyId__groupId) */
  const [drafts, setDrafts] = useState({});
  /** @type {Array<{ partyId: string, groupId: string, partyName: string, groupName: string, city?: string, gst?: string }>} */
  const [listRows, setListRows] = useState([]);

  const [setOpen, setSetOpen] = useState(false);
  const [popupMode, setPopupMode] = useState(/** @type {"group"|"party"} */ ("group"));
  const [popupGroupId, setPopupGroupId] = useState("");
  const [popupPartyId, setPopupPartyId] = useState("");
  const [popupGroupSearch, setPopupGroupSearch] = useState("");
  const [popupPartySearch, setPopupPartySearch] = useState("");
  const [popupAmount, setPopupAmount] = useState("");
  const [popupNote, setPopupNote] = useState("");
  const [popupSaving, setPopupSaving] = useState(false);

  const loadGen = useRef(0);

  const partyById = useMemo(() => {
    const m = new Map();
    for (const p of parties) m.set(p.id, p);
    return m;
  }, [parties]);

  const groupById = useMemo(() => {
    const m = new Map();
    for (const g of groups) m.set(g.id, g);
    return m;
  }, [groups]);

  const resolveGroupFromQuery = useCallback((q, groupList = groupsRef.current) => {
    const s = String(q || "").trim().toLowerCase();
    if (!s) return null;
    const exact = groupList.find((g) => g.name.toLowerCase() === s || g.id.toLowerCase() === s);
    if (exact) return exact;
    const starts = groupList.find((g) => g.name.toLowerCase().startsWith(s));
    if (starts) return starts;
    return groupList.find((g) => g.name.toLowerCase().includes(s)) || null;
  }, []);

  const resolvePartyFromQuery = useCallback((q, partyList = partiesRef.current) => {
    const s = String(q || "").trim().toLowerCase();
    if (!s) return null;
    const exact = partyList.find(
      (p) =>
        p.partyName.toLowerCase() === s || p.id.toLowerCase() === s || p.gst.toLowerCase() === s
    );
    if (exact) return exact;
    const starts = partyList.find((p) => p.partyName.toLowerCase().startsWith(s));
    if (starts) return starts;
    return (
      partyList.find(
        (p) =>
          p.partyName.toLowerCase().includes(s) ||
          p.city.toLowerCase().includes(s) ||
          p.gst.toLowerCase().includes(s)
      ) || null
    );
  }, []);

  const fetchAndApplyList = useCallback(async (filter, groupId, partyId) => {
    const gen = ++loadGen.current;
    setLoading(true);
    try {
      let rows = [];
      if (filter === "group" && groupId) {
        rows = await loadDiscountRowsForGroup(groupId);
      } else if (filter === "party" && partyId) {
        rows = await loadDiscountRowsForParty(partyId);
      } else if (filter === "none") {
        const all = await loadAllPartyDiscountRows();
        rows = all.filter((r) => Number(r.discountAmount) > 0);
      } else {
        rows = [];
      }
      if (gen !== loadGen.current) return;
      const { nextList, nextDrafts } = buildListFromRows(
        rows,
        partiesRef.current,
        groupsRef.current
      );
      setListRows(nextList);
      setDrafts(nextDrafts);
    } catch (e) {
      if (gen !== loadGen.current) return;
      console.error(e);
      toast.error(e?.message || "Failed to load discounts");
      setListRows([]);
      setDrafts({});
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, []);

  /** Masters + initial list — once on mount only */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [partySnap, groupSnap] = await Promise.all([
          getDocs(collection(db, "parties")),
          getDocs(collection(db, "groups")),
        ]);
        if (cancelled) return;
        const list = partySnap.docs
          .map((d) => normalizePartyRow(d.id, d.data()))
          .filter((p) => String(p.inactive || "").toLowerCase() !== "yes")
          .sort((a, b) => a.partyName.localeCompare(b.partyName, undefined, { sensitivity: "base" }));
        const groupList = groupSnap.docs
          .map((d) => normalizeGroupRow(d.id, d.data()))
          .filter((g) => g.name)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        partiesRef.current = list;
        groupsRef.current = groupList;
        setParties(list);
        setGroups(groupList);

        const all = await loadAllPartyDiscountRows();
        if (cancelled) return;
        const { nextList, nextDrafts } = buildListFromRows(
          all.filter((r) => Number(r.discountAmount) > 0),
          list,
          groupList
        );
        setListRows(nextList);
        setDrafts(nextDrafts);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error(e?.message || "Failed to load");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Reload list only when search filter actually changes (not on every keystroke without a match) */
  const skipFilterEffect = useRef(true);
  useEffect(() => {
    if (skipFilterEffect.current) {
      skipFilterEffect.current = false;
      return;
    }
    if (activeFilter === "group" && !matchedGroupId) return;
    if (activeFilter === "party" && !matchedPartyId) return;
    fetchAndApplyList(activeFilter, matchedGroupId, matchedPartyId);
  }, [activeFilter, matchedGroupId, matchedPartyId, fetchAndApplyList]);

  const reloadList = useCallback(() => {
    fetchAndApplyList(activeFilter, matchedGroupId, matchedPartyId);
  }, [fetchAndApplyList, activeFilter, matchedGroupId, matchedPartyId]);

  const onGroupSearchChange = (value) => {
    setGroupQuery(value);
    setPartyQuery("");
    setMatchedPartyId("");
    const g = resolveGroupFromQuery(value);
    if (g) {
      setMatchedGroupId(g.id);
      setActiveFilter("group");
    } else if (!String(value).trim()) {
      setMatchedGroupId("");
      setActiveFilter("none");
    } else {
      setMatchedGroupId("");
      setActiveFilter("group");
    }
  };

  const onPartySearchChange = (value) => {
    setPartyQuery(value);
    setGroupQuery("");
    setMatchedGroupId("");
    const p = resolvePartyFromQuery(value);
    if (p) {
      setMatchedPartyId(p.id);
      setActiveFilter("party");
    } else if (!String(value).trim()) {
      setMatchedPartyId("");
      setActiveFilter("none");
    } else {
      setMatchedPartyId("");
      setActiveFilter("party");
    }
  };

  const setDraftField = (key, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value, dirty: true },
    }));
  };

  const saveRow = async (row) => {
    const key = `${row.partyId}__${row.groupId}`;
    const draft = drafts[key];
    if (!draft) return;
    const amount = parseAmountInput(draft.discountAmount);
    if (Number.isNaN(amount)) {
      toast.error("Enter a valid discount amount");
      return;
    }
    setSavingId(key);
    try {
      await savePartyDiscountAmount({
        partyId: row.partyId,
        partyName: row.partyName,
        groupId: row.groupId,
        groupName: row.groupName,
        discountAmount: amount,
        note: draft.note,
      });
      setDrafts((prev) => ({
        ...prev,
        [key]: { ...prev[key], dirty: false },
      }));
      toast.success(`Saved ${row.partyName} · ${row.groupName}`);
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSavingId("");
    }
  };

  const dirtyCount = useMemo(
    () => Object.values(drafts).filter((d) => d?.dirty).length,
    [drafts]
  );

  const saveAllDirty = async () => {
    const dirtyKeys = Object.keys(drafts).filter((k) => drafts[k]?.dirty);
    if (!dirtyKeys.length) {
      toast.error("No unsaved changes");
      return;
    }
    setBulkSaving(true);
    let ok = 0;
    for (const key of dirtyKeys) {
      const [partyId, groupId] = key.split("__");
      const row = listRows.find((r) => r.partyId === partyId && r.groupId === groupId);
      if (!row) continue;
      const amount = parseAmountInput(drafts[key].discountAmount);
      if (Number.isNaN(amount)) continue;
      try {
        await savePartyDiscountAmount({
          partyId: row.partyId,
          partyName: row.partyName,
          groupId: row.groupId,
          groupName: row.groupName,
          discountAmount: amount,
          note: drafts[key].note,
        });
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const k of dirtyKeys) {
        if (next[k]) next[k] = { ...next[k], dirty: false };
      }
      return next;
    });
    setBulkSaving(false);
    toast.success(`Saved ${ok} row(s)`);
  };

  const openSetPopup = () => {
    setPopupMode(activeFilter === "party" ? "party" : "group");
    setPopupGroupId(matchedGroupId || "");
    setPopupPartyId(matchedPartyId || "");
    setPopupGroupSearch(matchedGroupId ? groupById.get(matchedGroupId)?.name || "" : "");
    setPopupPartySearch(matchedPartyId ? partyById.get(matchedPartyId)?.partyName || "" : "");
    setPopupAmount("");
    setPopupNote("");
    setSetOpen(true);
  };

  const popupGroupOptions = useMemo(() => {
    const q = popupGroupSearch.trim().toLowerCase();
    if (!q) return groups.slice(0, 80);
    return groups.filter((g) => g.name.toLowerCase().includes(q) || g.id.toLowerCase().includes(q)).slice(0, 80);
  }, [groups, popupGroupSearch]);

  const popupPartyOptions = useMemo(() => {
    const q = popupPartySearch.trim().toLowerCase();
    if (!q) return parties.slice(0, 80);
    return parties
      .filter(
        (p) =>
          p.partyName.toLowerCase().includes(q) ||
          p.id.toLowerCase().includes(q) ||
          p.gst.toLowerCase().includes(q) ||
          p.city.toLowerCase().includes(q)
      )
      .slice(0, 80);
  }, [parties, popupPartySearch]);

  const saveFromPopup = async () => {
    let groupId = popupGroupId;
    let partyId = popupPartyId;
    if (!groupId) {
      const g = resolveGroupFromQuery(popupGroupSearch);
      if (g) groupId = g.id;
    }
    if (!partyId) {
      const p = resolvePartyFromQuery(popupPartySearch);
      if (p) partyId = p.id;
    }
    if (!groupId) {
      toast.error("Select a group");
      return;
    }
    if (!partyId) {
      toast.error("Select a party");
      return;
    }
    const amount = parseAmountInput(popupAmount);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error("Enter a valid discount amount");
      return;
    }
    const group = groupById.get(groupId);
    const party = partyById.get(partyId);
    setPopupSaving(true);
    try {
      await savePartyDiscountAmount({
        partyId,
        partyName: party?.partyName || "",
        groupId,
        groupName: group?.name || "",
        discountAmount: amount,
        note: popupNote,
      });
      toast.success(`Saved ₹ ${fmtAmt(amount)} · ${party?.partyName} · ${group?.name}`);
      setSetOpen(false);
      await reloadList();
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setPopupSaving(false);
    }
  };

  const exportExcel = () => {
    if (!listRows.length) {
      toast.error("Nothing to export");
      return;
    }
    const data = listRows.map((r) => {
      const key = `${r.partyId}__${r.groupId}`;
      return {
        partyId: r.partyId,
        partyName: r.partyName,
        groupId: r.groupId,
        groupName: r.groupName,
        city: r.city,
        gst: r.gst,
        discountAmount: parseAmountInput(drafts[key]?.discountAmount) || 0,
        note: drafts[key]?.note || "",
      };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Discounts");
    XLSX.writeFile(wb, "party-discount-amounts.xlsx");
    toast.success("Download started");
  };

  const filterLabel =
    activeFilter === "group" && matchedGroupId
      ? `Group: ${groupById.get(matchedGroupId)?.name || matchedGroupId}`
      : activeFilter === "party" && matchedPartyId
        ? `Party: ${partyById.get(matchedPartyId)?.partyName || matchedPartyId}`
        : "All saved discounts (amount > 0)";

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Masters</p>
            <h1 className="text-2xl font-bold text-slate-900">Party discount amounts</h1>
            <p className="mt-1 text-sm text-slate-600">
              Search by <strong>group</strong> or <strong>party</strong> — list updates below. Use{" "}
              <strong>Set discount</strong> to add/edit in a popup.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openSetPopup}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Set discount
            </button>
            <button
              type="button"
              onClick={exportExcel}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <Download className="h-4 w-4" aria-hidden />
              Export
            </button>
            <button
              type="button"
              onClick={saveAllDirty}
              disabled={bulkSaving || dirtyCount === 0}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              <Save className="h-4 w-4" aria-hidden />
              {bulkSaving ? "Saving…" : `Save all (${dirtyCount})`}
            </button>
          </div>
        </div>

        {/* Two searches — same page, no tabs */}
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-600">
              Search group
            </span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                list="pda-group-search-list"
                value={groupQuery}
                onChange={(e) => onGroupSearchChange(e.target.value)}
                onFocus={() => {
                  if (groupQuery.trim()) onGroupSearchChange(groupQuery);
                }}
                placeholder="Type group name…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm shadow-sm focus:ring-2 focus:ring-indigo-300"
              />
              <datalist id="pda-group-search-list">
                {groups.map((g) => (
                  <option key={g.id} value={g.name} />
                ))}
              </datalist>
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-600">
              Search party
            </span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                list="pda-party-search-list"
                value={partyQuery}
                onChange={(e) => onPartySearchChange(e.target.value)}
                onFocus={() => {
                  if (partyQuery.trim()) onPartySearchChange(partyQuery);
                }}
                placeholder="Type party name…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm shadow-sm focus:ring-2 focus:ring-indigo-300"
              />
              <datalist id="pda-party-search-list">
                {parties.map((p) => (
                  <option key={p.id} value={p.partyName} />
                ))}
              </datalist>
            </div>
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700">
            {filterLabel}
            {activeFilter === "group" && groupQuery.trim() && !matchedGroupId ? (
              <span className="text-amber-700"> — no matching group</span>
            ) : null}
            {activeFilter === "party" && partyQuery.trim() && !matchedPartyId ? (
              <span className="text-amber-700"> — no matching party</span>
            ) : null}
          </p>
          <button
            type="button"
            onClick={reloadList}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden />
            Refresh
          </button>
        </div>

        <div className="mt-4 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-800 text-[10px] font-bold uppercase tracking-wide text-slate-100">
              <tr>
                <th className="px-3 py-2">Party</th>
                <th className="px-3 py-2">Group</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2 text-right">Discount (₹)</th>
                <th className="px-3 py-2">Note</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : listRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No discount rows. Search a group or party, or click <strong>Set discount</strong>.
                  </td>
                </tr>
              ) : (
                listRows.map((row) => {
                  const key = `${row.partyId}__${row.groupId}`;
                  const draft = drafts[key] || { discountAmount: "", note: "", dirty: false };
                  const isSaving = savingId === key;
                  return (
                    <tr
                      key={key}
                      className={`border-t border-slate-100 ${draft.dirty ? "bg-amber-50/50" : "odd:bg-white even:bg-slate-50/40"}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{row.partyName}</div>
                        <div className="font-mono text-[10px] text-slate-500">{row.partyId}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{row.groupName}</div>
                        <div className="font-mono text-[10px] text-slate-500">{row.groupId}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{row.city || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={draft.discountAmount}
                          onChange={(e) => setDraftField(key, "discountAmount", e.target.value)}
                          className="w-28 rounded-lg border border-slate-200 px-2 py-1.5 text-right text-sm tabular-nums"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={draft.note}
                          onChange={(e) => setDraftField(key, "note", e.target.value)}
                          className="w-full min-w-[8rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                          placeholder="Optional"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => saveRow(row)}
                          disabled={isSaving || bulkSaving}
                          className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-900 disabled:opacity-50"
                        >
                          {isSaving ? "…" : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {listRows.length} row(s) · {PARTY_DISCOUNTS_COLLECTION}
        </p>
      </main>

      {/* Full popup — Set discount */}
      {setOpen && (
        <div className="fixed inset-0 z-[80] flex items-stretch justify-center bg-black/50 p-2 sm:p-4">
          <div
            className="flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pda-set-title"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-6">
              <div>
                <h2 id="pda-set-title" className="text-lg font-bold text-slate-900">
                  Set discount
                </h2>
                <p className="text-xs text-slate-500">Choose group and party, enter amount, then Save.</p>
              </div>
              <button
                type="button"
                onClick={() => setSetOpen(false)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto px-4 py-4 sm:px-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setPopupMode("group")}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                    popupMode === "group" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  Focus: find group first
                </button>
                <button
                  type="button"
                  onClick={() => setPopupMode("party")}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                    popupMode === "party" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  Focus: find party first
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase text-slate-600">Group</label>
                  <input
                    type="search"
                    value={popupGroupSearch}
                    onChange={(e) => {
                      setPopupGroupSearch(e.target.value);
                      const g = resolveGroupFromQuery(e.target.value);
                      setPopupGroupId(g?.id || "");
                    }}
                    placeholder="Search group…"
                    className="mb-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    autoFocus={popupMode === "group"}
                  />
                  <div className="max-h-48 overflow-auto rounded-xl border border-slate-200">
                    {popupGroupOptions.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => {
                          setPopupGroupId(g.id);
                          setPopupGroupSearch(g.name);
                        }}
                        className={`block w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 ${
                          popupGroupId === g.id ? "bg-indigo-100 font-semibold" : ""
                        }`}
                      >
                        {g.name}
                      </button>
                    ))}
                    {!popupGroupOptions.length ? (
                      <p className="px-3 py-4 text-center text-xs text-slate-400">No groups</p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold uppercase text-slate-600">Party</label>
                  <input
                    type="search"
                    value={popupPartySearch}
                    onChange={(e) => {
                      setPopupPartySearch(e.target.value);
                      const p = resolvePartyFromQuery(e.target.value);
                      setPopupPartyId(p?.id || "");
                    }}
                    placeholder="Search party…"
                    className="mb-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    autoFocus={popupMode === "party"}
                  />
                  <div className="max-h-48 overflow-auto rounded-xl border border-slate-200">
                    {popupPartyOptions.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setPopupPartyId(p.id);
                          setPopupPartySearch(p.partyName);
                        }}
                        className={`block w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 ${
                          popupPartyId === p.id ? "bg-indigo-100 font-semibold" : ""
                        }`}
                      >
                        <span className="font-medium">{p.partyName}</span>
                        {p.city ? <span className="text-slate-400"> · {p.city}</span> : null}
                      </button>
                    ))}
                    {!popupPartyOptions.length ? (
                      <p className="px-3 py-4 text-center text-xs text-slate-400">No parties</p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold uppercase text-slate-600">
                    Discount amount (₹)
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={popupAmount}
                    onChange={(e) => setPopupAmount(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm tabular-nums"
                    placeholder="0.00"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-bold uppercase text-slate-600">Note</span>
                  <input
                    type="text"
                    value={popupNote}
                    onChange={(e) => setPopupNote(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                    placeholder="Optional"
                  />
                </label>
              </div>

              {(popupGroupId || popupPartyId) && (
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  Will save:{" "}
                  <strong>{partyById.get(popupPartyId)?.partyName || "— select party —"}</strong>
                  {" × "}
                  <strong>{groupById.get(popupGroupId)?.name || "— select group —"}</strong>
                  {popupAmount !== "" ? (
                    <>
                      {" "}
                      → ₹ <strong>{fmtAmt(parseAmountInput(popupAmount) || 0)}</strong>
                    </>
                  ) : null}
                </p>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-6">
              <button
                type="button"
                onClick={() => setSetOpen(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveFromPopup}
                disabled={popupSaving}
                className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {popupSaving ? "Saving…" : "Save discount"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
